<template>
  <div>
    <tx-action txType="Unstake" />
    <div class="d-flex justify-center mt-16">
      <div class="deposit-box pa-10">
        <c-tx-steps
          class="mt-6"
          borderType="stake"
          :options="steps"
          :current="currentStep"
          @setStep="s => (currentStep = s)"
        />
        <div v-if="currentStep === 1">
          <c-loading v-if="delegationsState.fetching" />
          <select-ecosystem :ecosystem="ecosystem" />
          <div class="d-flex mt-16">
            <div>
              <div class="text-5 font-weight-medium">Total Staked</div>
              <div class="mt-2 text-2">
                <div class="d-flex align-center">
                  <span class="primary--text">{{
                    delegatedBalance | denomFormat
                  }}</span>
                  <span class="gray--text text-uppercase ml-1">{{
                    currentProject.staking.token
                  }}</span>
                  <img
                    :src="
                      ($vuetify.theme.dark
                        ? currentProject.logoSmallDark
                        : currentProject.logoSmall) || currentProject.logoSmall
                    "
                    height="36"
                    width="36"
                    class="ml-4"
                  />
                </div>
              </div>
              <div class="mt-1 darkgray--text text-5">
                ${{ balanceUsd | currency }}
              </div>
            </div>
          </div>
          <div class="mt-10">
            <v-select
              :items="delegations"
              v-model="form.delegationId"
              item-value="delegationId"
              class="c-input search-stake mt-3"
              placeholder="Select request to unstake"
              outlined
              hide-details
            >
              <template v-slot:item="{ item }">
                <div class="flex-1 d-flex align-center justify-space-between">
                  <div class="text-4">
                    #{{ item.delegationId }} - {{ item.validator.name }}
                  </div>
                  <div class="text-5  gray--text">
                    <span>
                      {{ item.amount | denomFormat }}
                    </span>
                    <span class="ml-1">
                      {{ currentProject.staking.token | uppercase }}
                    </span>
                  </div>
                </div>
              </template>
              <template v-slot:selection="{ item }">
                <div
                  class="flex-1 d-flex align-center justify-space-between selection-item"
                >
                  <div class="text-4">
                    #{{ item.delegationId }} - {{ item.validator.name }}
                  </div>
                  <div class="text-5 gray--text">
                    <span>
                      {{ item.amount | denomFormat }}
                    </span>
                    <span class="ml-1">
                      {{ currentProject.staking.token | uppercase }}
                    </span>
                  </div>
                </div>
              </template>
            </v-select>
          </div>
          <div class="mt-6">
            <i class="text-4 font-weight-light">
              <span class="darkgray--text">Note: </span
              ><span class="gray--text"
                >Unstaking requests cannot be made within 72 hours of the end of
                the month.</span
              >
            </i>
          </div>
          <div class="mt-16">
            <c-btn
              class="width-100"
              @click="next"
              :disabled="btnNextDisabled"
              walletRequired
              >Continue</c-btn
            >
          </div>
        </div>
        <confirm
          v-if="currentStep === 2"
          class="mt-16"
          :delegation="selectedDelegation"
          @close="$emit('close')"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import AppConst from "@/common/constants";
import TxAction from "@/views/stake/components/tx/TxAction.vue";
import SelectProjects from "@/views/stake/components/tx/SelectProjects.vue";
import EnterAmount from "@/views/stake/components/tx/EnterAmount.vue";
import Confirm from "./Confirm.vue";
import differenceInHours from "date-fns/differenceInHours";
import endOfMonth from "date-fns/endOfMonth";
import SelectEcosystem from "@/views/stake/components/tx/SelectEcosystem.vue";

export default {
  components: {
    TxAction,
    SelectProjects,
    EnterAmount,
    Confirm,
    SelectEcosystem,
  },
  computed: {
    ...mapGetters({
      walletSelected: "userWallets/walletSelected",
      currentProject: "projectConfig/latestProject",
      getTokenPriceInUsd: "appConfig/getTokenPriceInUsd",
      stakingBalances: "staking/skale/stakingBalances",
      delegationsState: "staking/skale/delegations",
    }),
    delegatedBalance() {
      return this.stakingBalances.delegated ?? 0;
    },
    delegations() {
      return (
        this.delegationsState.result?.filter(
          item => item.state === AppConst.SKALE_TX_STATE.DELEGATED
        ) || []
      );
    },
    selectedDelegation() {
      return this.delegations.find(
        item => item.delegationId === this.form.delegationId
      );
    },
    balanceUsd() {
      return (
        this.getTokenPriceInUsd(this.currentProject.staking.token) *
        this.delegatedBalance
      );
    },
    amountUsd() {
      return (
        this.getTokenPriceInUsd(this.currentProject.staking.token) *
        this.form.amount
      );
    },
    validDaysRemaining() {
      return (
        this.selectedDelegation &&
        this.getHoursRemaining(this.selectedDelegation) >= 72
      );
    },
    btnNextDisabled() {
      if (!this.currentProject.status?.active) return true;
      return !this.selectedDelegation || !this.validDaysRemaining;
    },
    btnStakeDisabled() {
      return this.gasOption.gas <= 0 || this.gasOption.gasPrice <= 0;
    },
  },
  data: () => ({
    ecosystem: "Ethereum",
    steps: [
      {
        step: 1,
        text: "Select delegation",
      },
      {
        step: 2,
        text: "Confirm",
      },
    ],
    currentStep: 1,
    form: {
      amount: 0,
      delegationId: null,
    },
    gasOption: {},
  }),
  methods: {
    next() {
      this.currentStep = 2;
    },
    getHoursRemaining(delegation) {
      const now = new Date().valueOf();
      return differenceInHours(endOfMonth(now), now);
    },
  },
  watch: {
    ["walletSelected.address"]() {
      this.currentStep = 1;
      this.form.amount = 0;
    },
  },
};
</script>

<style lang="scss">
.selection-item + input {
  display: none;
}
</style>
